// Analytics event layer — Google Tag Manager only.
//
// The GTM container (build-time env REACT_APP_GTM_ID, rendered into public/index.html) owns
// every Google Analytics 4 / Google Ads tag. Nothing in the app loads gtag.js or defines
// window.gtag: each helper below pushes a plain object onto window.dataLayer, where the
// container's Custom Event triggers pick it up. With no container installed the pushes are
// harmless (dataLayer is just an array) — analytics must never break UX.
//
// Event contract (mirrored by GTM triggers/variables — do not rename):
//   generate_lead   { method, segment, lead_type, system }
//   tap_to_call     { source }
//   book_measure    { lead_id, date, slot }
//   spa_page_view   { page_path, page_title, page_location } — once per route view, initial load included
//                   (components/PageViewTracker.js); the Google tag in GTM runs with send_page_view off
//
// Pre-estimate calculator funnel (brief 01 §4) — fired by components/EstimateWizard.js and
// components/estimate/EstimateResults.js. Legacy events above keep firing alongside these.
//   calc_start             { entry_path, device }            first interaction with the calculator
//   calc_step              { step_index, question_key, answer_value }
//   calc_back              { step_index }                    index of the step the user left
//   calc_down_interstitial { choice: book | call | continue }
//   calc_form_view         {}
//   calc_form_submit       { timeline, system_wanted }
//   calc_result_view       { estimate_id, category, recommended_tier }
//   calc_pdf_download      { estimate_id }
//   calc_book_click        { estimate_id }
//   calc_call_click        { estimate_id | null }
//   calc_abandon           { last_step_index }               visibilitychange→hidden / beforeunload, started but unfinished
//   calc_email_submit      { estimate_id }                   open mode: "Email me the PDF" card (brief 02)
//   calc_phone_submit      { estimate_id }                   open mode: "Book my free measure" card (brief 02)
//   calc_rebate_view / calc_tco_view / calc_zip_submit / calc_zip_skip / calc_utility_choice   (Prompt 05)
//   calc_financing_view    { estimate_id, term_key }        Prompt 06: financing card rendered (once per estimate)
//   calc_term_switch       { term_key }                     Prompt 06: term chip tapped (updates every tier)
//   calc_lender_click      { estimate_id, lender, tier_selected, term_key }   HFS brief: a lender button on the results (also fires financing_click)
//   calc_link_copy         { estimate_id }                   Prompt 07: "Copy link" on /estimate/{id}
//   calc_estimate_reopen   { estimate_id, expired }          Prompt 07: /estimate/{id} opened outside the original session
//   calc_estimate_refresh  { parent_estimate_id, estimate_id }  Prompt 07: expired estimate re-run → new linked estimate
//   every calc_* event also carries  variant: 'open' | 'gated'  (brief 02 A/B switch)
//
// Prompt 11 — /hvac-cost/<city> pages:
//   cost_page_view        { city }                emitted once per /hvac-cost/<city> view
//   cost_page_calc_click  { city, sqft }           the page's "Get my exact price" launcher was clicked
//   calc_start's entry_path shows "/hvac-cost/<city>" when the calculator opens from one of these pages
//   (see entryPath() below); the lead/estimate record also carries entry_preset: true + that source_page.

export function trackEvent(eventName, params = {}) {
  try {
    if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
    window.dataLayer.push({ event: eventName, ...params });
    // Console breadcrumb for verifying events — development builds only.
    if (process.env.NODE_ENV === 'development') {
      console.info('[analytics]', eventName, params);
    }
  } catch (e) {
    // Analytics must never break UX - log in dev for observability, then swallow.
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] event suppressed:', e);
    }
  }
}

export const trackCalculatorSubmit = (leadType, systemKey) =>
  trackEvent('generate_lead', { method: 'estimate_calculator', lead_type: leadType, system: systemKey || 'commercial' });

export const trackCall = (source) => trackEvent('tap_to_call', { source });

/** HFS Financing brief §6 — every lender button click: lender id ('acorn' | 'hfs' | …) + the page it was on. */
export const trackFinancingClick = (lender, placement = 'page') => {
  let pagePath = '';
  try { pagePath = window.location.pathname; } catch (e) { pagePath = ''; }
  trackEvent('financing_click', { lender, page_path: pagePath, placement });
};

/** Route view — fired exactly once per route view (first load + client-side navigations), after the route's <title> is set. */
export const trackPageView = ({ pagePath, pageTitle, pageLocation }) =>
  trackEvent('spa_page_view', { page_path: pagePath, page_title: pageTitle, page_location: pageLocation });

/* ------------------------- calculator funnel helpers ------------------------- */
// brief 02: every calc_* event carries the arm the visitor is in ('open' | 'gated').
let calcVariant = null;
export const setCalcVariant = (v) => { calcVariant = v === 'gated' ? 'gated' : v === 'open' ? 'open' : null; };
export const getCalcVariant = () => calcVariant;
const calcEvent = (name, params = {}) => trackEvent(name, { variant: calcVariant, ...params });

/** 'mobile' (<768px) | 'tablet' (<1024px) | 'desktop' — matches the Tailwind breakpoints the UI uses. */
export const deviceClass = () => {
  try {
    if (window.matchMedia('(max-width: 767px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 1023px)').matches) return 'tablet';
  } catch (e) { /* no matchMedia (tests) */ }
  return 'desktop';
};

/** Path of the page that led here: the last INTERNAL page the visitor viewed in this SPA session
 * (set by components/PageViewTracker.js on every route change) takes priority, since React Router
 * navigation never updates document.referrer — Prompt 11 needs this to show a /hvac-cost/<city>
 * launcher as the calculator's entry_path. Falls back to the original full-page referrer (same-site
 * path, other-site host, or 'direct') for a visitor's very first page of the session. */
export const entryPath = () => {
  try {
    const internal = sessionStorage.getItem('idb_prev_path');
    if (internal) return internal;
  } catch (e) { /* storage blocked */ }
  try {
    if (!document.referrer) return 'direct';
    const ref = new URL(document.referrer);
    return ref.origin === window.location.origin ? ref.pathname : ref.host;
  } catch (e) {
    return 'direct';
  }
};

/** Prompt 11 — /hvac-cost/<city> page view and its calculator-launcher click. */
export const costPageView = (slug) => trackEvent('cost_page_view', { city: slug });
export const costPageCalcClick = (slug, sqftBucket) => trackEvent('cost_page_calc_click', { city: slug, sqft: sqftBucket || null });

export const calc = {
  start: () => calcEvent('calc_start', { entry_path: entryPath(), device: deviceClass() }),
  step: (stepIndex, questionKey, answerValue) => calcEvent('calc_step', { step_index: stepIndex, question_key: questionKey, answer_value: answerValue }),
  back: (stepIndex) => calcEvent('calc_back', { step_index: stepIndex }),
  downInterstitial: (choice) => calcEvent('calc_down_interstitial', { choice }),
  formView: () => calcEvent('calc_form_view', {}),
  formSubmit: (timeline, systemWanted) => calcEvent('calc_form_submit', { timeline: timeline || null, system_wanted: systemWanted || null }),
  resultView: (estimateId, category, recommendedTier) => calcEvent('calc_result_view', { estimate_id: estimateId || null, category, recommended_tier: recommendedTier || null }),
  pdfDownload: (estimateId) => calcEvent('calc_pdf_download', { estimate_id: estimateId || null }),
  bookClick: (estimateId) => calcEvent('calc_book_click', { estimate_id: estimateId || null }),
  callClick: (estimateId) => calcEvent('calc_call_click', { estimate_id: estimateId || null }),
  abandon: (lastStepIndex) => calcEvent('calc_abandon', { last_step_index: lastStepIndex }),
  // brief 02 — open-mode progressive capture
  emailSubmit: (estimateId) => calcEvent('calc_email_submit', { estimate_id: estimateId || null }),
  phoneSubmit: (estimateId) => calcEvent('calc_phone_submit', { estimate_id: estimateId || null }),
  // brief 03 — optional address step + county assessor prefill
  addressView: () => calcEvent('calc_address_view', {}),
  addressSkip: () => calcEvent('calc_address_skip', {}),
  addressSubmit: () => calcEvent('calc_address_submit', {}),
  prefillSuccess: (county, fieldsFilled) => calcEvent('calc_prefill_success', { county: county || null, fields_filled: fieldsFilled || 0 }),
  prefillFail: (reason) => calcEvent('calc_prefill_fail', { reason: reason || 'no_match' }),
  prefillEdit: (field) => calcEvent('calc_prefill_edit', { field }),
  // Prompt 05 — utility lookup, rebates, operating cost
  zipSubmit: (zip, utility) => calcEvent('calc_zip_submit', { zip: zip || null, utility: utility || null }),
  zipSkip: () => calcEvent('calc_zip_skip', {}),
  utilityChoice: (utility) => calcEvent('calc_utility_choice', { utility }),
  rebateView: (utility, low, high) => calcEvent('calc_rebate_view', { utility: utility || null, rebate_low: low ?? null, rebate_high: high ?? null }),
  tcoView: (estimateId, utility) => calcEvent('calc_tco_view', { estimate_id: estimateId || null, utility: utility || null }),
  // Prompt 06 + HFS brief — monthly payment estimates + the lender buttons (Acorn / HFS)
  financingView: (estimateId, termKey) => calcEvent('calc_financing_view', { estimate_id: estimateId || null, term_key: termKey || null }),
  termSwitch: (termKey) => calcEvent('calc_term_switch', { term_key: termKey }),
  lenderClick: (estimateId, lender, tierSelected, termKey) => calcEvent('calc_lender_click', { estimate_id: estimateId || null, lender: lender || null, tier_selected: tierSelected || null, term_key: termKey || null }),
  // Prompt 07 — persistent estimate links (/estimate/{id})
  linkCopy: (estimateId) => calcEvent('calc_link_copy', { estimate_id: estimateId || null }),
  estimateReopen: (estimateId, expired) => calcEvent('calc_estimate_reopen', { estimate_id: estimateId || null, expired: !!expired }),
  estimateRefresh: (parentEstimateId, newEstimateId) => calcEvent('calc_estimate_refresh', { parent_estimate_id: parentEstimateId || null, estimate_id: newEstimateId || null }),
  // Prompt 08 — optional nameplate / panel photos read by the vision model
  photoView: () => calcEvent('calc_photo_view', {}),
  photoSkip: () => calcEvent('calc_photo_skip', {}),
  photoUpload: (kind) => calcEvent('calc_photo_upload', { kind }),
  photoReadOk: (kind, confidenceBucket) => calcEvent('calc_photo_read_ok', { kind, confidence_bucket: confidenceBucket || null }),
  photoReadFail: (reason, kind) => calcEvent('calc_photo_read_fail', { reason: reason || 'unreadable', kind: kind || null }),
  photoApply: (kind, field) => calcEvent('calc_photo_apply', { kind, field }),
  // Prompt 09 — commercial / industrial budget branch
  commercialStart: () => calcEvent('calc_commercial_start', { entry_path: entryPath(), device: deviceClass() }),
  commercialStep: (stepIndex, questionKey, answerValue) => calcEvent('calc_commercial_step', { step_index: stepIndex, question_key: questionKey, answer_value: answerValue }),
  commercialGateSubmit: (workEmail, timeline) => calcEvent('calc_commercial_gate_submit', { work_email: !!workEmail, timeline: timeline || null }),
  commercialResultView: (estimateId, equipment, units, available) => calcEvent('calc_commercial_result_view', { estimate_id: estimateId || null, equipment: equipment || null, units: units || null, available: !!available }),
  commercialSitewalkRequest: (estimateId, week) => calcEvent('calc_commercial_sitewalk_request', { estimate_id: estimateId || null, preferred_week: week || null }),
  // Prompt 10 — published hit rate + Range Promise (both only fire when the block is actually on screen)
  hitRateView: (estimateId, pct, placement) => calcEvent('calc_hit_rate_view', { estimate_id: estimateId || null, hit_rate_pct: pct ?? null, placement: placement || 'results' }),
  rangePromiseView: (estimateId, version, placement) => calcEvent('calc_range_promise_view', { estimate_id: estimateId || null, promise_version: version || null, placement: placement || 'results' }),
  rangePromiseTermsClick: (estimateId, version) => calcEvent('calc_range_promise_terms_click', { estimate_id: estimateId || null, promise_version: version || null }),
};
